import routeNames from '@/router/names'
import * as find from './find'
import getName, {
  defaultModeName,
  baseModeName,
  modeFootprint,
  shortModeName,
} from './getName'
import canHavePresets from './canHavePresets'

function buildSiblingsReferences(result) {
  let previous = null
  for (let i = 0; i < result.length; i += 1) {
    const item = result[i]
    if (item) {
      // Set the next preset as null by default
      item.next = null
      // Set the previous preset
      item.previous = previous
      if (item.previous) {
        // Set the next preset of the previous preset
        item.previous.next = item
      }
    }
    previous = item
  }
}

function findReportItems(reportData, kind, uid) {
  const result = {
    infos: [],
    warnings: [],
    errors: [],
  }
  for (let i = 0; i < reportData.infos.length; i += 1) {
    const info = reportData.infos[i]
    if (info[kind]?._uid === uid) {
      result.infos.push(info)
    }
  }
  for (let i = 0; i < reportData.warnings.length; i += 1) {
    const warning = reportData.warnings[i]
    if (warning[kind]?._uid === uid) {
      result.warnings.push(warning)
    }
  }
  for (let i = 0; i < reportData.errors.length; i += 1) {
    const error = reportData.errors[i]
    if (error[kind]?._uid === uid) {
      result.errors.push(error)
    }
  }

  return result
}

const presetIconPrefix = 'scanlibrary/icon/presets/'

export function buildIndex(item) {
  const indexes = []

  if (item.name) {
    indexes.push(item.name)
  }

  if (item.tags) {
    indexes.push(...item.tags)
  }

  if (item._category) {
    indexes.push(item._category)
  }

  if (item.icon) {
    let icon = item.icon.toLowerCase()
    /*
    Only keep the relevant part of the icon path for the search index.
    Otherwise all presets would match for searches like: "scanLibrary", "icon" or "presets"
    or any substrings of those values ("scan", "library", "reset")
    */
    if (icon.startsWith(presetIconPrefix)) {
      icon = icon.slice(presetIconPrefix.length)
    }
    indexes.push(icon)
  }

  if (item._icon) {
    indexes.push(item._icon)
  }

  if (item._color?.index) {
    indexes.push(item._color.index)
  }
  if (item._color?.family) {
    indexes.push(item._color.family)
  }
  if (item._color?.hexCode) {
    indexes.push(item._color.hexCode)
  }

  return indexes.join(' ').toLowerCase()
}

export const globalChannel = (profile, channel, reportData, linkTemplate = {}) => {
  if (profile && channel) {
    const link = {
      name: linkTemplate.name || routeNames.GlobalChannel,
      params: {
        profileUid: profile.uid,
        ...(linkTemplate.params || {}),
        channelUid: channel._uid,
      },
    }
    const linkName = getName(channel)

    const channelWithIndex = {
      ...channel,
      searchIndex: buildIndex(channel),
    }

    return {
      data: channelWithIndex,
      canHavePresets: canHavePresets(channelWithIndex),
      report: findReportItems(reportData, 'channel', channel._uid, link, linkName),
      link,
      linkName,
    }
  }
  return null
}

export const globalChannels = (profile, reportData, linkTemplate = {}) => {
  if (profile && Array.isArray(profile.channels)) {
    const result = profile.channels.map(
      (channel) => globalChannel(profile, channel, reportData, linkTemplate),
    )
    buildSiblingsReferences(result)
    return result
  }
  return null
}

export const globalPresetRange = (channel, presetRange, linkTemplate = {}) => {
  if (channel && presetRange) {
    const link = {
      name: linkTemplate.name || routeNames.GlobalPresetRange,
      params: {
        ...channel.link.params,
        ...(linkTemplate.params || {}),
        presetRangeUid: presetRange._uid,
      },
    }
    const linkName = getName(presetRange)
    return {
      data: presetRange,
      report: findReportItems(channel.report, 'presetRange', presetRange._uid, link, linkName),
      dmxRange: channel.data.has_2_presets ? channel.data.dmx_ranges[presetRange._order] : null,
      link,
      linkName,
      parent: channel,
    }
  }
  return null
}

export const globalPresetRanges = (channel, linkTemplate = {}) => {
  if (channel && Array.isArray(channel.data.presetRanges)) {
    const result = channel.data.presetRanges.map(
      (presetRange) => globalPresetRange(channel, presetRange, linkTemplate),
    )
    buildSiblingsReferences(result)
    return result
  }
  return null
}

export const globalPreset = (presetRange, preset, linkTemplate = {}) => {
  if (presetRange && preset) {
    const link = {
      name: linkTemplate.name || routeNames.GlobalPreset,
      params: {
        ...presetRange.link.params,
        ...(linkTemplate.params || {}),
        presetUid: preset._uid,
      },
    }
    const linkName = getName(preset)
    return {
      data: preset,
      report: findReportItems(presetRange.report, 'preset', preset._uid, link, linkName),
      link,
      linkName,
      parent: presetRange,
    }
  }
  return null
}

function parseReportItem(reportItem, profileUid) {
  const result = {
    message: reportItem.message,
  }

  const local = !!reportItem.mode
  if (local) {
    result.mode = {
      ...reportItem.mode,
      link: {
        name: routeNames.Mode,
        params: {
          profileUid,
          modeUid: reportItem.mode._uid,
        },
      },
    }

    if (reportItem.channel) {
      result.channel = {
        ...reportItem.channel,
        link: {
          name: routeNames.LocalChannel,
          params: {
            ...result.mode.link.params,
            channelUid: reportItem.channel._uid,
          },
        },
      }
    }
  } else {
    result.channel = {
      ...reportItem.channel,
      link: {
        name: routeNames.GlobalChannel,
        params: {
          profileUid,
          channelUid: reportItem.channel._uid,
        },
      },
    }
  }

  if (reportItem.presetRange) {
    result.presetRange = {
      ...reportItem.presetRange,
      link: {
        name: local ? routeNames.LocalPresetRange : routeNames.GlobalPresetRange,
        params: {
          ...result.channel.link.params,
          presetRangeUid: reportItem.presetRange._uid,
        },
      },
    }
  }

  if (reportItem.preset) {
    result.preset = {
      ...reportItem.preset,
      link: {
        name: local ? routeNames.LocalPreset : routeNames.GlobalPreset,
        params: {
          ...result.presetRange.link.params,
          presetUid: reportItem.preset._uid,
        },
      },
    }
  }

  result.target = {
    ...reportItem.target,
    link: result[reportItem.target.item].link,
  }

  return result
}

export function report(profile) {
  if (profile) {
    const profileReport = profile.report
    return {
      errors: profileReport.errors.map((error) => parseReportItem(error, profile.uid)),
      warnings: profileReport.warnings.map((warning) => parseReportItem(warning, profile.uid)),
      infos: profileReport.infos.map((info) => parseReportItem(info, profile.uid)),
    }
  }
  return {
    errors: [],
    warnings: [],
    infos: [],
  }
}

function parseImportReportItem(reportItem, profileUid) {
  const result = {
    message: reportItem.message,
  }

  const { path } = reportItem

  result.profile = {
    ...path.profile,
    _uid: path.profile.uid,
    link: {
      name: routeNames.Content,
      params: {
        profileUid,
      },
    },
  }

  const local = !!path.mode
  if (local) {
    result.mode = {
      ...path.mode,
      _uid: path.mode.uid,
      link: {
        name: routeNames.Mode,
        params: {
          profileUid,
          modeUid: path.mode.uid,
        },
      },
    }

    if (path.channel) {
      result.channel = {
        ...path.channel,
        _uid: path.channel.uid,
        link: {
          name: routeNames.LocalChannel,
          params: {
            ...result.mode.link.params,
            channelUid: path.channel.uid,
          },
        },
      }
    }
  } else if (path.channel) {
    result.channel = {
      ...path.channel,
      _uid: path.channel.uid,
      link: {
        name: routeNames.GlobalChannel,
        params: {
          profileUid,
          channelUid: path.channel.uid,
        },
      },
    }
  }

  if (path.presetRange) {
    result.presetRange = {
      ...path.presetRange,
      _uid: path.presetRange.uid,
      link: {
        name: local ? routeNames.LocalPresetRange : routeNames.GlobalPresetRange,
        params: {
          ...result.channel.link.params,
          presetRangeUid: path.presetRange.uid,
        },
      },
    }
  }

  if (path.preset) {
    result.preset = {
      ...path.preset,
      _uid: path.preset.uid,
      link: {
        name: local ? routeNames.LocalPreset : routeNames.GlobalPreset,
        params: {
          ...result.presetRange.link.params,
          presetUid: path.preset.uid,
        },
      },
    }
  }

  let targetLink
  let targetItem
  switch (reportItem.target.type) {
    case 'ssl.mode':
      targetItem = 'mode'
      targetLink = result.mode.link
      break
    case 'ssl.channel':
      targetItem = 'channel'
      targetLink = result.channel.link
      break
    case 'ssl.presetRange':
      targetItem = 'presetRange'
      targetLink = result.presetRange.link
      break
    case 'ssl.preset':
      targetItem = 'preset'
      targetLink = result.preset.link
      break
    default:
      targetItem = 'profile'
      targetLink = result.profile.link
  }

  result.target = {
    ...reportItem.target,
    item: targetItem,
    link: targetLink,
  }

  return {
    ...result.target,
    ...result,
  }
}

export function parseImportReport(importReport, profileUid) {
  const errors = []
  const warnings = []
  const infos = []
  const parseErrors = []
  importReport.forEach((warning) => {
    if (warning?.payload?.level) {
      try {
        switch (warning.payload.level) {
          case 'error':
            errors.push(parseImportReportItem(warning.payload, profileUid))
            break
          case 'warning':
            warnings.push(parseImportReportItem(warning.payload, profileUid))
            break
          case 'info':
            infos.push(parseImportReportItem(warning.payload, profileUid))
            break
          default:
        }
      } catch (e) {
        parseErrors.push(e)
      }
    }
  })
  return {
    errors,
    warnings,
    infos,
    parseErrors,
  }
}

export const globalPresets = (presetRange, linkTemplate = {}) => {
  if (presetRange && Array.isArray(presetRange.data.presets)) {
    const result = presetRange.data.presets.map(
      (preset) => globalPreset(presetRange, preset, linkTemplate),
    )
    buildSiblingsReferences(result)
    return result
  }
  return null
}

export const mode = (profile, modeData, reportData, linkTemplate = {}) => {
  if (profile && modeData) {
    const link = {
      name: linkTemplate.name || routeNames.Mode,
      params: {
        profileUid: profile.uid,
        ...(linkTemplate.params || {}),
        modeUid: modeData._uid,
      },
    }
    const linkName = getName(modeData)
    return {
      data: modeData,
      report: findReportItems(reportData, 'mode', modeData._uid, link, linkName),
      link,
      linkName,
      footprint: modeFootprint(modeData),
      defaultName: defaultModeName(modeData),
      shortName: shortModeName(modeData),
      baseName: baseModeName(modeData),
    }
  }
  return null
}

export const modes = (profile, reportData, linkTemplate = {}) => {
  if (profile && Array.isArray(profile.modes)) {
    const result = profile.modes.map(
      (modeData) => mode(profile, modeData, reportData, linkTemplate),
    )
    buildSiblingsReferences(result)
    return result
  }
  return null
}

const findGlobalChannel = (profile, localChannel) => {
  if (localChannel && localChannel._linkedChannel && profile) {
    return find.item(profile.channels, localChannel._linkedChannel)
  }
  return null
}

const mergeChannel = (channel, linkedChannel) => {
  if (linkedChannel || channel) {
    const local = channel || {}
    const { presetRanges, ...global } = linkedChannel || {}
    const result = {
      ...global,
      ...local,
    }
    // If it's not an LSB and there is no presetRanges
    // overrides on the local channel, add presetRanges
    if (!result.lsb && !result.presetRanges) {
      result.presetRanges = presetRanges
    }
    return result
  }
  return null
}

export const localChannel = (profile, modeData, channel, linkTemplate = {}) => {
  // We also need the profile here to find the linked global channel
  if (modeData && channel) {
    const local = channel
    const global = findGlobalChannel(profile, local)
    const link = {
      name: linkTemplate.name || routeNames.LocalChannel,
      params: {
        ...modeData.link.params,
        ...(linkTemplate.params || {}),
        channelUid: channel._uid,
      },
    }
    const mergedChannel = mergeChannel(local, global)
    const linkName = getName(mergedChannel)
    return {
      local,
      global,
      data: mergedChannel,
      canHavePresets: canHavePresets(mergedChannel),
      report: findReportItems(modeData.report, 'channel', local._uid, link, linkName),
      link,
      linkName,
      parent: modeData,
    }
  }
  return null
}

const findMsbChannel = (channels, lsbChannel) => {
  if (lsbChannel && lsbChannel.data.lsb && lsbChannel.data.index_16_bits && channels) {
    return find.parsedItem(channels, lsbChannel.data.index_16_bits)
  }
  return null
}

const findLsbChannel = (channels, msbChannel) => {
  if (msbChannel && msbChannel.data.msb && msbChannel.data.index_16_bits && channels) {
    return find.parsedItem(channels, msbChannel.data.index_16_bits)
  }
  return null
}

export const localChannels = (profile, modeData, linkTemplate = {}) => {
  // We also need the profile here to find the linked global channels
  if (modeData && Array.isArray(modeData.data.channels)) {
    const result = modeData.data.channels.map(
      (channel) => localChannel(profile, modeData, channel, linkTemplate),
    )
    for (let i = 0; i < result.length; i += 1) {
      // TODO: Optimize by indexing channels by their UID
      const channel = result[i]
      if (channel) {
        // Find the MSB channel of a LSB channel
        channel.msb = findMsbChannel(result, channel)
        // Find the LSB channel of a MSB channel
        channel.lsb = findLsbChannel(result, channel)
      }
    }
    buildSiblingsReferences(result)
    return result
  }
  return null
}

export const localPresetRange = (channel, presetRange, linkTemplate = {}) => {
  if (channel && presetRange) {
    const link = {
      name: linkTemplate.name || routeNames.LocalPresetRange,
      params: {
        ...channel.link.params,
        ...(linkTemplate.params || {}),
        presetRangeUid: presetRange._uid,
      },
    }
    const linkName = getName(presetRange)
    return {
      data: presetRange,
      report: findReportItems(channel.report, 'presetRange', presetRange._uid, link, linkName),
      dmxRange: channel.data.has_2_presets ? channel.data.dmx_ranges[presetRange._order] : null,
      link,
      linkName,
      parent: channel,
    }
  }
  return null
}

export const localPresetRanges = (channel, linkTemplate = {}) => {
  if (channel && Array.isArray(channel.data.presetRanges)) {
    const result = channel.data.presetRanges.map(
      (presetRange) => localPresetRange(channel, presetRange, linkTemplate),
    )
    buildSiblingsReferences(result)
    return result
  }
  return null
}

export const localPreset = (presetRange, preset, linkTemplate = {}) => {
  if (presetRange && preset) {
    const link = {
      name: linkTemplate.name || routeNames.LocalPreset,
      params: {
        ...presetRange.link.params,
        ...(linkTemplate.params || {}),
        presetUid: preset._uid,
      },
    }
    const linkName = getName(preset)
    return {
      data: preset,
      report: findReportItems(presetRange.report, 'preset', preset._uid, link, linkName),
      link,
      linkName,
      parent: presetRange,
    }
  }
  return null
}

export const localPresets = (presetRange, linkTemplate = {}) => {
  if (presetRange && Array.isArray(presetRange.data.presets)) {
    const result = presetRange.data.presets.map(
      (preset) => localPreset(presetRange, preset, linkTemplate),
    )
    buildSiblingsReferences(result)
    return result
  }
  return null
}

export const tags = (list) => {
  const references = {}
  const result = {
    list,
    references,
    defaultPresets: [],
  }
  for (let i = 0; i < list.length; i += 1) {
    const tagPath = list[i].split('/')
    let prev = result
    for (let j = 0; j < tagPath.length; j += 1) {
      const tagName = tagPath[j]
      const pathArr = prev.pathArr ? [...prev.pathArr, tagName] : [tagName]
      const path = pathArr.join('/')
      if (!prev.tree) {
        prev.tree = {}
      }
      const tagObj = prev.tree[tagName] || {
        name: tagName,
        pathArr,
        path,
        list: prev.list.filter((item) => item.startsWith(path)),
        parents: prev.parents ? [...prev.parents, prev] : [],
        defaultPresets: new Set(),
      }
      prev.tree[tagName] = tagObj
      prev = tagObj
      references[path] = tagObj
    }
  }
  return result
}
